import {
  getSpell,
  levelFraction,
  type PlayerStatus,
  type ProgressEvent,
  type ResourceEvent,
  SPELL_BAR_SIZE,
  type SpellDefinition,
  type SpellsEvent,
} from '@shefira/shared';

import {
  bindingLabel,
  bindingOf,
  isAction,
  type KeyAction,
  onBindingsChange,
  shortcutTitle,
} from '../settings/keybindings.js';
import { EffectStrip } from './effect-strip.js';
import { uiDecimal, uiFormat, uiText } from './text.js';

/**
 * The spell bar: the four spells the player chose in the grimoire (ADR 0048)
 * on keys 1 to 4, each in a slot frame with its icon, a cooldown sweep and
 * the key it answers to, over an energy gauge and, under it, the experience
 * gauge of the current level. An empty slot stays in sight and opens the
 * grimoire when clicked; its key does nothing.
 *
 * Intents only (CLAUDE.md rule 1): a key sends `cast` with the spell id and
 * nothing else. The energy and the cooldowns shown are the server's, as last
 * reported; between reports the sweep is only counted down locally, so the
 * bar looks alive without the client deciding anything.
 */
export interface SpellBarOptions {
  onCast: (spellId: string) => void;
  /** An empty slot clicked: the grimoire, to fill it. */
  onOpenGrimoire: () => void;
  /** The mount key's action (R by default): get on the worn mount, or off it. */
  onMount: () => void;
}

/** The four slots' actions, rebindable in the settings (digits by default). */
const SPELL_ACTIONS = [
  'spell1',
  'spell2',
  'spell3',
  'spell4',
] as const satisfies readonly KeyAction[];

export class SpellBar {
  private readonly root: HTMLElement;
  private readonly fill: HTMLElement;
  private readonly label: HTMLElement;
  private readonly xpFill: HTMLElement;
  private readonly xpLabel: HTMLElement;
  /** What each slot holds, as the server last settled the bar; undefined is empty. */
  private spells: (SpellDefinition | undefined)[] = Array.from(
    { length: SPELL_BAR_SIZE },
    () => undefined,
  );
  private readonly slots: HTMLButtonElement[] = [];
  private readonly icons: HTMLElement[] = [];
  private readonly sweeps: HTMLElement[] = [];
  /** Local clock (performance.now) at which each spell is ready again, by spell id. */
  private readonly readyAt = new Map<string, number>();
  private energy = Number.POSITIVE_INFINITY;
  /** The key hint under each slot, refreshed when the player rebinds a spell. */
  private readonly hints = new Map<KeyAction, HTMLElement>();
  private unbind: (() => void) | undefined;
  private frame = 0;
  /** After the spells: the worn mount, to get on and off with a click. */
  private readonly mount: HTMLButtonElement;
  /** The states the player carries, over the slots (ADR 0048). */
  private readonly effects = new EffectStrip();
  private mountState = '';
  /** Darkens the mount while the combat lock holds (W refused), shrinking as it runs out. */
  private readonly mountSweep: HTMLElement;
  private mountLock = -1;

  private readonly onKeyDown = (event: KeyboardEvent): void => {
    if (event.metaKey || event.ctrlKey || event.altKey) return;
    if (document.activeElement instanceof HTMLInputElement) return;
    if (event.repeat) return;
    const index = SPELL_ACTIONS.findIndex((action) => isAction(event, action));
    const spell = index === -1 ? undefined : this.spells[index];
    if (spell === undefined) return;
    event.preventDefault();
    this.options.onCast(spell.id);
  };

  constructor(private readonly options: SpellBarOptions) {
    this.root = document.createElement('section');
    this.root.className = 'spell-bar';

    const slots = document.createElement('div');
    slots.className = 'spell-bar__slots';
    SPELL_ACTIONS.forEach((action, index) => {
      const slot = document.createElement('button');
      slot.type = 'button';
      slot.className = 'spell-bar__slot';
      const hint = document.createElement('span');
      hint.className = 'spell-bar__key';
      hint.textContent = uiText(bindingLabel(bindingOf(action)), 'key');
      this.hints.set(action, hint);
      const icon = document.createElement('span');
      icon.className = 'spell-bar__icon';
      const sweep = document.createElement('span');
      sweep.className = 'spell-bar__sweep';
      sweep.hidden = true;
      slot.append(icon, sweep, hint);
      slot.addEventListener('click', () => {
        const spell = this.spells[index];
        if (spell === undefined) options.onOpenGrimoire();
        else options.onCast(spell.id);
      });
      this.slots.push(slot);
      this.icons.push(icon);
      this.sweeps.push(sweep);
      slots.append(slot);
    });
    this.mount = document.createElement('button');
    this.mount.type = 'button';
    this.mount.className = 'spell-bar__slot spell-bar__slot--mount';
    this.mount.hidden = true;
    const mountIcon = document.createElement('span');
    mountIcon.className = 'spell-bar__icon';
    mountIcon.style.backgroundImage = `url('/assets/ui/icon-mount.png')`;
    const mountHint = document.createElement('span');
    mountHint.className = 'spell-bar__key';
    mountHint.textContent = uiText(bindingLabel(bindingOf('mount')), 'key');
    this.hints.set('mount', mountHint);
    this.mountSweep = document.createElement('span');
    this.mountSweep.className = 'spell-bar__sweep';
    this.mountSweep.hidden = true;
    this.mount.append(mountIcon, this.mountSweep, mountHint);
    this.mount.addEventListener('click', () => {
      options.onMount();
    });
    slots.append(this.mount);

    const gauge = document.createElement('div');
    gauge.className = 'spell-bar__gauge';
    this.fill = document.createElement('span');
    this.fill.className = 'spell-bar__fill';
    this.label = document.createElement('span');
    this.label.className = 'spell-bar__label';
    gauge.append(this.fill, this.label);

    const xpGauge = document.createElement('div');
    xpGauge.className = 'spell-bar__gauge spell-bar__gauge--xp';
    this.xpFill = document.createElement('span');
    this.xpFill.className = 'spell-bar__fill';
    this.xpFill.style.width = '0%';
    this.xpLabel = document.createElement('span');
    this.xpLabel.className = 'spell-bar__label';
    xpGauge.append(this.xpFill, this.xpLabel);

    this.root.append(this.effects.root, slots, gauge, xpGauge);
    document.body.append(this.root);
    window.addEventListener('keydown', this.onKeyDown);
    // The hint under each slot, and the empty slots' tooltip, follow a rebinding.
    this.unbind = onBindingsChange(() => {
      for (const [action, hint] of this.hints) {
        hint.textContent = uiText(bindingLabel(bindingOf(action)), 'key');
      }
      this.renderSlots();
    });
    this.renderSlots();
    this.tick();
  }

  /** The server's word on the bar (ADR 0048): what each of the four slots holds. */
  setSpells(event: SpellsEvent): void {
    this.spells = Array.from({ length: SPELL_BAR_SIZE }, (_, index) => {
      const id = event.bar[index];
      return id === null || id === undefined ? undefined : getSpell(id);
    });
    this.renderSlots();
  }

  private renderSlots(): void {
    this.slots.forEach((slot, index) => {
      const spell = this.spells[index];
      const icon = this.icons[index];
      slot.classList.toggle('spell-bar__slot--empty', spell === undefined);
      if (spell === undefined) {
        const label = shortcutTitle('Emplacement libre : ouvrir le grimoire', 'spellbook');
        slot.title = uiText(label);
        slot.setAttribute('aria-label', uiText(label));
        if (icon !== undefined) icon.style.backgroundImage = '';
        slot.classList.remove('spell-bar__slot--starved');
        return;
      }
      slot.title = uiText(spellTooltip(spell));
      slot.setAttribute('aria-label', uiText(spell.name));
      if (icon !== undefined) icon.style.backgroundImage = `url('/assets/ui/${spell.icon}.png')`;
      slot.classList.toggle('spell-bar__slot--starved', this.energy < spell.cost);
    });
  }

  /** What is left of the combat lock (0 to 1): no getting on before it runs out. */
  setMountLock(share: number): void {
    const degrees = Math.round(Math.min(1, Math.max(0, share)) * 360);
    if (degrees === this.mountLock) return;
    this.mountLock = degrees;
    this.mountSweep.style.setProperty('--left', `${String(degrees)}deg`);
    this.mountSweep.hidden = degrees === 0;
  }

  /** A slot of another owner (the quick potion), placed after the spells and before the mount. */
  addSlot(slot: HTMLElement): void {
    this.mount.before(slot);
  }

  /** Shown while a mount is worn, lit while riding; its tooltip says which way it goes. */
  setMount(worn: boolean, riding: boolean): void {
    const state = `${String(worn)}:${String(riding)}`;
    if (state === this.mountState) return;
    this.mountState = state;
    this.mount.hidden = !worn;
    this.mount.classList.toggle('spell-bar__slot--riding', riding);
    const label = riding ? 'Descendre de ta monture' : 'Monter sur ta monture';
    const key = bindingLabel(bindingOf('mount'));
    this.mount.title = uiFormat('{label} ({key})', {
      label: uiText(label),
      key: uiText(key, 'key'),
    });
    this.mount.setAttribute('aria-label', uiText(label));
  }

  /** The server's word on energy and cooldowns, for every spell known. */
  setResource(resource: ResourceEvent): void {
    const share = resource.maxEnergy > 0 ? resource.energy / resource.maxEnergy : 0;
    this.fill.style.width = `${String(Math.round(share * 100))}%`;
    this.label.textContent = uiText(`${String(resource.energy)} / ${String(resource.maxEnergy)}`);
    this.energy = resource.energy;
    const now = performance.now();
    this.readyAt.clear();
    for (const [id, left] of Object.entries(resource.cooldowns)) {
      if (left > 0) this.readyAt.set(id, now + left);
    }
    this.renderSlots();
  }

  /** The server's word on experience: the gauge shows the current level only. */
  setProgress(progress: ProgressEvent): void {
    this.xpFill.style.width = `${String(Math.round(levelFraction(progress) * 100))}%`;
    const gained = progress.xp - progress.levelStartXp;
    const needed = progress.nextLevelXp - progress.levelStartXp;
    this.xpLabel.textContent =
      needed > 0
        ? uiFormat('Niv. {level} · {gained} / {needed} XP', {
            level: progress.level,
            gained,
            needed,
          })
        : uiFormat('Niv. {level}', { level: progress.level });
  }

  /** The sweep: a shadow over the icon that shrinks as the cooldown runs out. */
  private readonly tick = (): void => {
    const now = performance.now();
    this.sweeps.forEach((sweep, index) => {
      const spell = this.spells[index];
      const left = spell === undefined ? 0 : Math.max(0, (this.readyAt.get(spell.id) ?? 0) - now);
      const total = spell?.cooldownMs ?? 0;
      const share = total > 0 ? Math.min(1, left / total) : 0;
      sweep.style.setProperty('--left', `${String(Math.round(share * 360))}deg`);
      sweep.hidden = left <= 0;
    });
    this.effects.tick(now);
    this.frame = requestAnimationFrame(this.tick);
  };

  /** A state landed on the player (`status`), shown over the slots with its time left. */
  showEffect(status: PlayerStatus, durationMs: number): void {
    this.effects.show(status, durationMs);
  }

  /** The player fell: every state is gone with them. */
  clearEffects(): void {
    this.effects.clear();
  }

  destroy(): void {
    cancelAnimationFrame(this.frame);
    window.removeEventListener('keydown', this.onKeyDown);
    this.unbind?.();
    this.root.remove();
  }
}

/** « Charge — Tu bondis… (25 énergie, recharge 12 s) », the tooltip of a spell here and in the grimoire. */
export function spellTooltip(spell: SpellDefinition): string {
  return uiFormat('{name} — {description} ({cost})', {
    name: spell.name,
    description: spell.description,
    cost: spellCost(spell),
  });
}

/** « 25 énergie · recharge 12 s » */
export function spellCost(spell: SpellDefinition): string {
  const seconds = spell.cooldownMs / 1000;
  const cooldown = Number.isInteger(seconds) ? String(seconds) : uiDecimal(seconds, 1);
  return uiFormat('{cost} énergie · recharge {cooldown} s', { cost: spell.cost, cooldown });
}
